import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';

/**
 * App-scoped left-nav visibility (spec: app-scoped-left-nav).
 *
 * Per-item visibility in the app menu is dynamic (per-party effective
 * permissions) and lives inside each app's permission hook. Rather than
 * duplicate that policy in the shell, the app publishes the section keys it
 * deems visible for the current user/party, keyed by app key; the shell's
 * `AppNavMenu` renders only those. Until an app publishes, the menu shows its
 * declared items optimistically (the app's content area still gates access).
 */
interface AppNavState {
  /** appKey → visible section keys (e.g. `apps/pack-analysis` → ['ingest','browser']). */
  visibleByApp: Record<string, string[]>;
}

const initialState: AppNavState = {
  visibleByApp: {},
};

const appNavSlice = createSlice({
  name: 'appNav',
  initialState,
  reducers: {
    setAppNavVisibleSections(
      state,
      action: PayloadAction<{ appKey: string; sections: string[] }>,
    ) {
      state.visibleByApp[action.payload.appKey] = action.payload.sections;
    },
  },
});

export const { setAppNavVisibleSections } = appNavSlice.actions;

export const selectAppNavVisibleSections =
  (appKey: string) =>
  (state: RootState): string[] | undefined =>
    state.appNav.visibleByApp[appKey];

export default appNavSlice.reducer;
